import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Download, RefreshCw, Loader2, Video, FileText, Brain, ShieldCheck, AlertTriangle } from 'lucide-react';
import DashboardLayout from '../../../components/DashboardLayout';
import { useAuth } from '../../../context/AuthContext';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TONE = {
  uploaded: 'bg-lime-500/15 text-lime-500 border-lime-500/40', completed: 'bg-lime-500/15 text-lime-500 border-lime-500/40',
  pass: 'bg-lime-500/15 text-lime-500 border-lime-500/40', measured: 'bg-lime-500/15 text-lime-500 border-lime-500/40',
  failed: 'bg-red-500/15 text-red-500 border-red-500/40', fail: 'bg-red-500/15 text-red-500 border-red-500/40',
  blocked: 'bg-red-500/15 text-red-500 border-red-500/40', not_measured: 'bg-red-500/15 text-red-500 border-red-500/40',
  incomplete: 'bg-amber-500/15 text-amber-500 border-amber-500/40', pending_review: 'bg-amber-500/15 text-amber-500 border-amber-500/40',
};
export const StatusPill = ({ value, testId }) => (
  <Badge variant="outline" data-testid={testId} className={`text-[11px] ${TONE[value] || 'border-slate-500/40 text-slate-400'}`}>
    {(value || 'pending').replace(/_/g, ' ')}
  </Badge>
);

const Stat = ({ label, value, testId }) => (
  <div className="rounded-lg border border-border p-3">
    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className="text-lg font-semibold text-foreground mt-0.5" data-testid={testId}>{value ?? '—'}</p>
  </div>
);

const ProctoringCard = ({ pr = {} }) => {
  const m = pr.measurement_status === 'measured';
  const nm = (v, s = '') => (v === null || v === undefined || (!m && s !== 'raw') ? 'Not measured' : `${v}${s === 'raw' ? '' : s}`);
  const rows = [
    ['Face presence', nm(pr.face_presence_percent, '%')], ['Eye contact', nm(pr.eye_contact_percent, '%')],
    ['Face continuity match', nm(pr.face_match_percent, '%')], ['Face absent events', nm(pr.face_absent_count)],
    ['Multiple faces events', nm(pr.multiple_faces_count)], ['Face swap events', nm(pr.face_swap_events)],
    ['Multiple voices events', pr.audio_samples ? pr.multiple_voices_count : 'Not measured'],
    ['Tab switches', pr.tab_switch_count ?? 0], ['Window focus losses', pr.focus_loss_count ?? 0], ['Analysis samples', pr.samples ?? 0],
  ];
  return (
    <Card data-testid="v2-detail-proctoring">
      <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><ShieldCheck className="w-4 h-4" />Proctoring observations <StatusPill value={pr.measurement_status || 'pending'} testId="v2-detail-proctoring-status" /></CardTitle></CardHeader>
      <CardContent className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
        {rows.map(([k, v]) => (<div key={k} className="flex justify-between border-b border-border/50 py-1"><span className="text-muted-foreground">{k}</span><span className="font-medium">{v}</span></div>))}
      </CardContent>
    </Card>
  );
};

const QuestionCard = ({ q }) => {
  const v = q.video || {}; const t = q.transcription || {}; const e = q.evaluation || {};
  const ai = e.ai_analysis || {};
  return (
    <Card data-testid={`v2-detail-question-${q.sequence_number}`}>
      <CardContent className="p-5 grid lg:grid-cols-5 gap-5">
        <div className="lg:col-span-2">
          {v.status === 'uploaded' ? (
            <iframe title={`Q${q.sequence_number} recording`} src={v.embed_url} className="w-full aspect-video rounded-md border border-border bg-black" allow="autoplay" data-testid={`v2-detail-video-${q.sequence_number}`} />
          ) : (
            <div className="w-full aspect-video rounded-md border border-dashed border-red-500/40 flex flex-col items-center justify-center text-sm text-red-500 gap-1" data-testid={`v2-detail-video-missing-${q.sequence_number}`}>
              <AlertTriangle className="w-5 h-5" />Recording {String(v.status || 'not recorded').replace(/_/g, ' ')}
              {v.last_error && <span className="text-xs text-muted-foreground px-4 text-center">{v.last_error}</span>}
            </div>
          )}
          {v.drive_file_id && <p className="text-[11px] text-muted-foreground mt-1.5 font-mono break-all">Drive file {v.drive_file_id} · {Math.round((v.size_bytes || 0) / 1024)} KB · attempts {v.attempts}</p>}
        </div>
        <div className="lg:col-span-3 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <p className="font-medium text-foreground"><span className="text-lime-500 mr-1.5">Q{q.sequence_number}.</span>{q.question_text}</p>
            <span className="text-xl font-bold shrink-0" data-testid={`v2-detail-score-${q.sequence_number}`}>{e.status === 'completed' || e.status === 'not_answered' ? `${e.score ?? 0}%` : '—'}</span>
          </div>
          <div className="flex flex-wrap gap-1.5 items-center text-xs text-muted-foreground">
            <Badge variant="outline" className="text-[11px]">{q.category || 'General'}</Badge>
            <Video className="w-3.5 h-3.5 ml-1" /><StatusPill value={v.status || 'not_recorded'} testId={`v2-detail-video-status-${q.sequence_number}`} />
            <FileText className="w-3.5 h-3.5 ml-1" /><StatusPill value={t.status} testId={`v2-detail-transcript-status-${q.sequence_number}`} />
            <Brain className="w-3.5 h-3.5 ml-1" /><StatusPill value={e.status} testId={`v2-detail-eval-status-${q.sequence_number}`} />
          </div>
          <div className="rounded-md bg-muted/40 p-3 text-sm" data-testid={`v2-detail-transcript-${q.sequence_number}`}>
            {t.status === 'completed' ? t.text : <span className="text-muted-foreground italic">{t.error || (t.status === 'no_speech' ? 'No speech detected' : t.status === 'not_applicable' ? 'Not answered' : 'Transcript pending')}</span>}
          </div>
          {ai.overall_assessment && <p className="text-sm text-muted-foreground">{ai.overall_assessment}</p>}
          {e.error && <p className="text-xs text-red-500">{e.error}</p>}
        </div>
      </CardContent>
    </Card>
  );
};

export default function V2InterviewDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const auth = useCallback(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => axios.get(`${API_URL}/api/v2/interviews/${id}/detail`, auth()).then(r => setD(r.data)).catch(() => toast.error('Failed to load interview')), [id, auth]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!d || !['queued', 'processing'].includes(d.processing_status)) return;
    const t = setInterval(load, 4000); return () => clearInterval(t);
  }, [d, load]);

  const downloadPdf = async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API_URL}/api/v2/interviews/${id}/report.pdf`, { ...auth(), responseType: 'blob' });
      const url = URL.createObjectURL(r.data); const a = document.createElement('a');
      a.href = url; a.download = `${d.interview_number}_report.pdf`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error('PDF generation failed'); } finally { setBusy(false); }
  };
  const retry = async () => {
    try { await axios.post(`${API_URL}/api/v2/interviews/${id}/process`, {}, auth()); toast.success('Processing restarted'); setTimeout(load, 1000); }
    catch (e) { toast.error(e.response?.data?.detail || 'Retry failed'); }
  };

  if (!d) return <DashboardLayout role="super_admin"><div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin" /></div></DashboardLayout>;
  const snap = d.settings_snapshot || {};
  const processing = ['queued', 'processing'].includes(d.processing_status);
  return (
    <DashboardLayout role="super_admin">
      <div className="space-y-5" data-testid="v2-interview-detail">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="v2-detail-back"><ArrowLeft className="w-4 h-4" /></Button>
            <div>
              <h1 className="text-2xl font-bold text-foreground">{d.student_name} <span className="text-muted-foreground font-mono text-base">{d.interview_number}</span></h1>
              <p className="text-sm text-muted-foreground">{d.student_email} · {d.university_name || 'No university'} · Engine {String(d.engine_version).toUpperCase()}{d.is_test_interview ? ' · Test' : ''}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={retry} disabled={processing} data-testid="v2-detail-retry"><RefreshCw className={`w-4 h-4 mr-2 ${processing ? 'animate-spin' : ''}`} />{processing ? 'Processing…' : 'Retry processing'}</Button>
            <Button onClick={downloadPdf} disabled={busy} className="bg-lime-500 hover:bg-lime-600 text-black" data-testid="v2-detail-download-pdf"><Download className="w-4 h-4 mr-2" />PDF report</Button>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 items-center text-sm">
          Result <StatusPill value={d.overall_result} testId="v2-detail-result" /> Processing <StatusPill value={d.processing_status} testId="v2-detail-processing-status" /> Completion <StatusPill value={d.termination_reason || d.status} testId="v2-detail-completion" />
        </div>
        {d.result_note && <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-600" data-testid="v2-detail-result-note">{d.result_note}</div>}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <Stat label="Overall score" value={d.overall_score !== null && d.overall_score !== undefined ? `${d.overall_score}%` : 'Not calculated'} testId="v2-detail-overall-score" />
          <Stat label="Configured" value={snap.configured_question_count} testId="v2-detail-configured" />
          <Stat label="Planned" value={d.planned_question_count} testId="v2-detail-planned" />
          <Stat label="Presented" value={d.presented_question_count} />
          <Stat label="Answered" value={d.answered_question_count} testId="v2-detail-answered" />
          <Stat label="Duration" value={d.duration_seconds != null ? `${Math.floor(d.duration_seconds / 60)}m ${d.duration_seconds % 60}s` : '—'} />
        </div>
        {(d.failure_reasons || []).length > 0 && (
          <Card data-testid="v2-detail-failure-reasons"><CardContent className="p-4 text-sm"><p className="font-semibold mb-1">Failure reasons</p>{d.failure_reasons.map(r => <p key={r} className="text-red-500">• {r}</p>)}</CardContent></Card>
        )}
        {(d.evidence_issues || []).length > 0 && (
          <Card data-testid="v2-detail-evidence-issues"><CardContent className="p-4 text-sm"><p className="font-semibold mb-1">Missing evidence / processing failures</p>{d.evidence_issues.map(r => <p key={r} className="text-amber-600">• {r}</p>)}</CardContent></Card>
        )}
        <ProctoringCard pr={d.proctoring_summary || {}} />
        <div className="space-y-3">{d.questions_detail.map(q => <QuestionCard key={q.question_instance_id} q={q} />)}</div>
      </div>
    </DashboardLayout>
  );
}
